import React, { useState, useEffect } from 'react';
import {
  Container, SimpleGrid, Card, Text, Group, ThemeIcon,
  Button, Title, Stack, Paper, RingProgress, Center, Badge
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBarcode, IconList, IconTool, IconPrinter, IconChartBar,
  IconPlus, IconUsers, IconUserPlus, IconSettings, IconMail,
  IconDeviceDesktop, IconAlertCircle, IconArchive, IconBriefcase
} from '@tabler/icons-react';
import { api } from '../../api/CheckApi';
import AddDeviceModal from '../../components/AddDeviceModal';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addDeviceOpened, { open: openAddDevice, close: closeAddDevice }] = useDisclosure(false);

  const [statistics, setStatistics] = useState({
    total_devices: 0,
    available_devices: 0,
    assigned_devices: 0,
    maintenance_devices: 0,
    retired_devices: 0,
    total_users: 0,
    active_users: 0,
    admin_users: 0,
    total_site_workers: 0,
    active_assignments: 0
  });

  useEffect(() => {
    const role = (localStorage.getItem('user_role') || '').toLowerCase();
    setIsAdmin(
      role === 'admin' ||
      role === 'yönetici' ||
      role === 'it' ||
      role === 'bt' ||
      role === 'bilgi işlem' ||
      role === 'bilgi_islem'
    );
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const data = await api.getStatistics();
      setStatistics(data);
    } catch (error) {
      console.error('İstatistikler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const dashboardCards = [
    {
      title: 'Barkod Tara',
      description: 'Cihaz barkodlarını tarayarak bilgileri anında görüntüle.',
      icon: IconBarcode,
      action: () => navigate('/scan'),
      color: 'violet',
    },
    {
      title: 'Cihaz Listesi',
      description: 'Cihaz envanterini kategori ve türe göre detaylı incele.',
      icon: IconList,
      action: () => navigate('/devices'),
      color: 'teal',
    },
    {
      title: 'IT İşlemleri',
      description: 'Bakım, onarım ve diğer IT işlemlerini kaydet, takip et.',
      icon: IconTool,
      action: () => navigate('/it-operations'),
      color: 'orange',
    },
    {
      title: 'Barkod Yazdır',
      description: 'Sisteme yeni eklenen cihazlar için barkod etiketleri oluştur.',
      icon: IconPrinter,
      action: () => navigate('/barcode-printing'),
      color: 'gray',
    },
    {
      title: 'Raporlar',
      description: 'Kullanım ve atama geçmişi raporlarını grafiklerle analiz et.',
      icon: IconChartBar,
      action: () => navigate('/reports'),
      color: 'blue',
    }
  ];

  if (isAdmin) {
    dashboardCards.push(
      {
        title: 'Yeni Cihaz',
        description: 'Envantere yeni IT ekipmanı veya donanım ekle.',
        icon: IconPlus,
        action: openAddDevice,
        color: 'green',
      },
      {
        title: 'Şantiye Personeli',
        description: 'Sahada görev yapan şantiye ekiplerinin kayıtlarını yönet.',
        icon: IconUserPlus,
        action: () => navigate('/site-worker-management'),
        color: 'cyan',
      },
      {
        title: 'Sistem Ayarları',
        description: 'Roller, modüller ve genel sistem yapılandırmalarını düzenle.',
        icon: IconSettings,
        action: () => navigate('/settings'),
        color: 'red',
      }
    );
  }

  const availabilityRate = statistics.total_devices > 0
    ? Math.round((statistics.available_devices / statistics.total_devices) * 100)
    : 0;

  const usageRate = statistics.total_devices > 0
    ? Math.round((statistics.assigned_devices / statistics.total_devices) * 100)
    : 0;

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">

        <Group justify="space-between" align="flex-end">
          <div>
            <Title
              order={1}
              fw={900}
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 45 }}
            >
              IT Envanter ve Cihaz Takip Merkezi
            </Title>
          </div>
          <Badge size="lg" color={isAdmin ? 'red' : 'blue'} variant="light">
            {isAdmin ? 'Yönetici Oturumu' : 'Personel Oturumu'}
          </Badge>
        </Group>

        <Paper withBorder p="md" radius="md" shadow="sm">
          <Group justify="space-between" mb="lg">
            <Text fw={700} size="lg">Hızlı İstatistikler</Text>
            <Button variant="subtle" size="xs" onClick={loadStatistics} loading={loading}>
              Verileri Yenile
            </Button>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="lg">
            <Card withBorder radius="md" p="md">
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed" fw={500}>Toplam Cihaz</Text>
                <ThemeIcon color="blue" variant="light" size="lg">
                  <IconDeviceDesktop size={20} />
                </ThemeIcon>
              </Group>
              <Text fw={700} size="xl">{statistics.total_devices}</Text>
              <Group gap="xs" mt="md">
                <Badge color="teal" variant="dot">{statistics.available_devices} Müsait</Badge>
                <Badge color="orange" variant="dot">{statistics.assigned_devices} Atanmış</Badge>
              </Group>
            </Card>

            <Card withBorder radius="md" p="md">
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed" fw={500}>Kritik Durumdaki Cihazlar</Text>
                <ThemeIcon color="red" variant="light" size="lg">
                  <IconAlertCircle size={20} />
                </ThemeIcon>
              </Group>
              <Text fw={700} size="xl">
                {statistics.maintenance_devices + statistics.retired_devices}
              </Text>
              <Group gap="xs" mt="md">
                <Badge color="red" variant="light">{statistics.maintenance_devices} Bakımda</Badge>
                <Badge color="gray" variant="light">{statistics.retired_devices} Emekli</Badge>
              </Group>
            </Card>

            <Card withBorder radius="md" p="md">
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed" fw={500}>Personel Özeti</Text>
                <ThemeIcon color="indigo" variant="light" size="lg">
                  <IconUsers size={20} />
                </ThemeIcon>
              </Group>
              <Text fw={700} size="xl">{statistics.total_users + statistics.total_site_workers}</Text>
              <Group gap="xs" mt="md">
                <Badge color="blue" variant="light">{statistics.active_users} Merkez</Badge>
                <Badge color="cyan" variant="light">{statistics.total_site_workers} Şantiye</Badge>
              </Group>
            </Card>

            <Card withBorder radius="md" p="xs">
              <Group justify="center" gap="xl">
                <RingProgress
                  size={100}
                  roundCaps
                  thickness={8}
                  sections={[{ value: availabilityRate, color: 'teal' }]}
                  label={
                    <Center>
                      <Text fw={700} size="sm">{availabilityRate}%</Text>
                    </Center>
                  }
                />
                <Stack gap={0}>
                  <Text size="sm" fw={700}>Müsaitlik</Text>
                  <Text size="xs" c="dimmed">Envanterdeki boş cihaz oranı</Text>
                </Stack>
              </Group>
            </Card>
          </SimpleGrid>
        </Paper>

        <Title order={3} mt="md">Modüller ve İşlemler</Title>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {dashboardCards.map((card, index) => (
            <Card
              key={index}
              withBorder
              padding="lg"
              radius="md"
              className="hover-card"
              style={{
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onClick={card.action}
            >
              <Group justify="space-between" mt="md" mb="xs">
                <ThemeIcon color={card.color} variant="light" size={50} radius="md">
                  <card.icon size={28} stroke={1.5} />
                </ThemeIcon>
              </Group>

              <Text fw={700} mt="md" size="lg">
                {card.title}
              </Text>

              <Text
                c="dimmed"
                size="sm"
                mt="sm"
                style={{ flexGrow: 1, minHeight: '40px' }}
              >
                {card.description}
              </Text>

              <Button variant="light" color={card.color} fullWidth mt="md" radius="md">
                Giriş Yap
              </Button>
            </Card>
          ))}
        </SimpleGrid>

        {isAdmin && (
          <Paper
            mt="lg"
            p="xl"
            radius="md"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <Group justify="space-between">
              <div>
                <Group gap="sm" mb="xs">
                  <IconMail size={24} />
                  <Title order={3}>Email Bildirim Merkezi</Title>
                </Group>
                <Text size="sm" style={{ opacity: 0.8 }} mb="md">
                  Personellere veya şantiye ekiplerine toplu bilgilendirme yapın, şablonlarınızı yönetin.
                </Text>
                <Button
                  variant="white"
                  color="violet"
                  onClick={() => navigate('/email-management')}
                >
                  Yönetim Paneline Git
                </Button>
              </div>

              <Group gap="lg" className="d-none d-md-flex">
                <Stack align="center" gap={5}>
                  <ThemeIcon size={40} radius="xl" color="white" variant="light">
                    <IconBriefcase size={20} />
                  </ThemeIcon>
                  <Text size="xs" fw={700}>Toplu Gönderim</Text>
                </Stack>

                <Stack align="center" gap={5}>
                  <ThemeIcon size={40} radius="xl" color="white" variant="light">
                    <IconArchive size={20} />
                  </ThemeIcon>
                  <Text size="xs" fw={700}>Şablonlar</Text>
                </Stack>
              </Group>
            </Group>
          </Paper>
        )}
      </Stack>

      <AddDeviceModal
        opened={addDeviceOpened}
        onClose={closeAddDevice}
        onSuccess={() => {
          loadStatistics();
        }}
      />
    </Container>
  );
};

export default Dashboard;